/**
 * Represents a custom HTML element for a variant button.
 * Initially loads the variant options based on the variant data passed in from the component
 * When the variant button is clicked, it updates the variant options based on the selected variant
 * It also updates the price, gallery image, and sets the data-variant attribute.
 * This is used in the product template.
 * @class VariantButton
 * @extends HTMLElement
 */

class VariantButton extends HTMLElement {
  /**
   * Constructs a new instance of the VariantButton class.
   */
  constructor() {
    super();

    /*-------------------------- INITIALIZE DATA -----------------------------------*/

    const parseAttribute = (attribute) => {
      // Swap the single-quote string delimiters to double quotes BEFORE
      // decoding the /quote/ and /apos/ placeholders — content quotes are
      // still placeholders at that point, so only delimiters are affected.
      return JSON.parse(
        attribute
          .replace(/'/g, '"')
          .replace(/\/quote\//g, '\\"')
          .replace(/\/apos\//g, "'")
      );
    };

    // Get the primary product info
    this.productInfo = parseAttribute(this.getAttribute('js-product'));
    // Get the variants
    this.variants = parseAttribute(this.getAttribute('js-variants'));
    // Get the flavors data
    this.flavors = parseAttribute(this.getAttribute('js-flavors'));

    // Get the first available variant, this is required to determine which option is selected on default load
    this.firstAvailableVariant = parseAttribute(
      this.getAttribute('js-first-available-variant')
    );

    //Initialize on variant change event when the variant button is clicked
    this.addEventListener('click', this.onVariantChange);
    // Add keyboard event listener
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  // Add new keyboard handler method
  handleKeydown(event) {
    if (
      event.target.tagName === 'LABEL' &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      event.target.click();
    }
  }

  async connectedCallback() {
    try {
      // NEW: Check for a selected flavor radio input and update firstAvailableVariant accordingly.
      const flavorRadio = this.querySelector(
        'input[name="select-option1"]:checked'
      );
      if (flavorRadio) {
        // If js-first-available-variant was empty or not matching, update it.
        if (!this.firstAvailableVariant || !this.firstAvailableVariant[0]) {
          this.firstAvailableVariant = [{ option1: flavorRadio.value }];
        } else {
          this.firstAvailableVariant[0].option1 = flavorRadio.value;
        }
      }

      // NEW: Check for a pre-selected size from server-side rendering
      const sizeRadio = this.querySelector(
        'input[name="select-option2"]:checked'
      );

      // Check if URL has a size parameter
      const urlParams = new URLSearchParams(window.location.search);
      const urlSize = urlParams.get('size');

      if (
        sizeRadio &&
        this.firstAvailableVariant &&
        this.firstAvailableVariant[0]
      ) {
        // Size was pre-selected (matched from URL)
        this.firstAvailableVariant[0].option2 = sizeRadio.value;
        // Preserve size URL parameter on initial load if size is pre-selected
        // Do this before renderOptions() to ensure URL is set
        this.updateSizeUrlParam(sizeRadio.value);
      } else if (urlSize) {
        // URL has size parameter but it didn't match any available size
        // Clear the URL parameter since the size doesn't exist for this product
        // Also clear it from flavor links so it doesn't persist when navigating
        this.updateSizeUrlParam(null);
      }

      // Proceed with rendering options
      this.renderOptions();

      // Scroll to selected flavor on mobile (horizontal scroll)
      this.scrollToSelectedFlavor();

      // If a size was pre-selected, we'll initialize variant in the setTimeout below

      // --- Ensure button is disabled on initial load if no valid variant is selected ---
      setTimeout(() => {
        // Initialize options array from checked radio buttons (including pre-selected size)
        this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = this.fieldsets.map((fieldset) => {
          const checkedRadio = Array.from(
            fieldset.querySelectorAll('input')
          ).find((radio) => radio.checked);
          return checkedRadio ? checkedRadio.value : null;
        });

        const smartCartButton = document.querySelector('.smart-cart-button');
        const overlay = document.querySelector('.smart-cart-overlay');

        // Check if a valid variant is selected
        let validVariant = false;
        if (this.productInfo.options.length === 1) {
          validVariant = !!this.firstAvailableVariant[0]?.option1;
        } else if (this.productInfo.options.length === 2) {
          validVariant =
            !!this.firstAvailableVariant[0]?.option1 &&
            !!this.firstAvailableVariant[0]?.option2;
        } else if (this.productInfo.options.length > 2) {
          validVariant =
            !!this.firstAvailableVariant[0]?.option1 &&
            !!this.firstAvailableVariant[0]?.option2 &&
            !!this.firstAvailableVariant[0]?.option3;
        }

        // Set initial description (will use priority: variant → flavor → default)
        // Set currentVariant for initial load to enable variant-level description check
        if (
          this.firstAvailableVariant &&
          this.firstAvailableVariant.length > 0
        ) {
          const initialOptions = this.firstAvailableVariant.map((opt) => {
            const key = Object.keys(opt)[0];
            return opt[key];
          });
          this.currentVariant = this.variants.find((variant) => {
            return !variant.options
              .map((option, index) => {
                return initialOptions[index] === option;
              })
              .includes(false);
          });

          // If a variant is found (including when size is pre-selected), update button and image
          if (this.currentVariant) {
            this.getCurrentVariant();
          } else if (!validVariant && smartCartButton && overlay) {
            smartCartButton.classList.add('pointer-events-none');
            overlay.classList.add('pointer-events-none');
            overlay.innerHTML =
              this.getAttribute('default-button-text') || 'Select Size';
          }
        }
        this.updateDescription();

        // Setup flavor link popup handlers
        this.setupFlavorLinkPopupHandlers();

        // Wire bev-link click tracking onto direct buy CTAs (custom button)
        this.setupDirectCtaTracking();

        // Publish the page-load "viewed beverage" event now that
        // currentVariant has been resolved above.
        this.trackBeverageView();

        // --- Set default UPC for selected flavor if no size is selected (initial load) ---
        const selectedFlavor = this.firstAvailableVariant[0]?.option1;
        const shopInStoreButton = document.querySelector(
          '.shop-in-store-button'
        );
        const flavorDefaultUpc = this.flavors[selectedFlavor]?.default_upc;

        if (
          flavorDefaultUpc &&
          this.productInfo.options.length > 1 &&
          !this.firstAvailableVariant[0]?.option2
        ) {
          if (shopInStoreButton) {
            let originalHref =
              shopInStoreButton.dataset.originalHref ||
              shopInStoreButton.getAttribute('href');
            if (!shopInStoreButton.dataset.originalHref) {
              shopInStoreButton.dataset.originalHref = originalHref;
            }
            if (originalHref.includes('sc-upc=')) {
              shopInStoreButton.setAttribute(
                'href',
                originalHref.replace(
                  /sc-upc=[^&]+/,
                  'sc-upc=' + flavorDefaultUpc
                )
              );
            } else if (originalHref.includes('?')) {
              shopInStoreButton.setAttribute(
                'href',
                originalHref + '&sc-upc=' + flavorDefaultUpc
              );
            } else {
              shopInStoreButton.setAttribute(
                'href',
                originalHref + '?sc-upc=' + flavorDefaultUpc
              );
            }
          }
          if (smartCartButton) {
            smartCartButton.setAttribute('data-sc-upc', flavorDefaultUpc);
          }
        }
      }, 100); // Short delay to ensure DOM is ready
    } catch (err) {
      // Error fetching product data
    }
  }

  /**
   * Scrolls to the selected flavor on mobile (horizontal scroll)
   */
  scrollToSelectedFlavor() {
    // Only scroll on mobile where horizontal scrolling is enabled
    const scrollContainer = this.querySelector('.overflow-x-auto');
    if (!scrollContainer) return;

    // Find the flavor link that matches the current product URL
    const currentUrl = window.location.href;
    const currentHandle = currentUrl.split('/products/')[1]?.split('?')[0];

    if (!currentHandle) return;

    // Find the link that matches the current product handle
    const allFlavorLinks = scrollContainer.querySelectorAll('a.button-swatch');
    let selectedFlavorLink = null;

    for (const link of allFlavorLinks) {
      const href = link.getAttribute('href');
      if (href && href.includes(`/products/${currentHandle}`)) {
        selectedFlavorLink = link;
        break;
      }
    }

    if (!selectedFlavorLink) return;

    // Use requestAnimationFrame with a small delay to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Scroll the selected flavor into view, centered if possible
        selectedFlavorLink.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 100);
    });
  }

  /**
   * Returns the beverage category code (IT / SFW / OG / Energy) for Klaviyo
   * affinity modeling, derived from the product template suffix rendered onto
   * this element. Falls back to the raw suffix so the value is never empty.
   * NOTE: confirm this map if the soda / mountain-water split ever changes.
   */
  getBevCategory() {
    const suffix = (this.dataset.templateSuffix || '').toLowerCase();
    const map = {
      'iced-tea': 'IT',
      'sparkling-energy': 'Energy',
      'mountain-water': 'OG',
      'mountain-water-keg': 'OG',
    };
    if (map[suffix]) return map[suffix];
    // All flavored-sparkling* templates map to SFW.
    if (suffix.indexOf('flavored-spark') === 0) return 'SFW';
    return suffix || null;
  }

  /**
   * Extracts a clean retailer name from a SmartCommerce dropdown <li>.
   * SmartCommerce renders the retailer as a logo image (alt="<Retailer> logo")
   * inside a row whose aria-label is "<button text> - <Retailer>"; the visible
   * text is just the CTA label ("View"), so we read the logo/aria-label first.
   */
  getRetailerName(li) {
    if (!li) return null;
    // Preferred: the logo image's alt, e.g. "Instacart logo" -> "Instacart".
    const logo = li.querySelector('img[alt]');
    if (logo) {
      const alt = logo.getAttribute('alt').replace(/\s*logo\s*$/i, '').trim();
      if (alt) return alt;
    }
    // Fallback: aria-label like "Select Size - Instacart" -> "Instacart".
    const labelled = li.querySelector('[aria-label]');
    if (labelled) {
      const parts = labelled.getAttribute('aria-label').split(' - ');
      const name = parts[parts.length - 1].trim();
      if (name) return name;
    }
    // Last resort: visible text (often just the CTA label, e.g. "View").
    return li.textContent.replace(/\s+/g, ' ').trim() || null;
  }

  /**
   * Derives a friendly retailer name from a direct buy-link URL's hostname,
   * e.g. https://www.amazon.com/dp/... -> "Amazon".
   */
  getRetailerFromUrl(url) {
    if (!url) return null;
    try {
      const host = new URL(url, window.location.origin).hostname
        .replace(/^www\./, '')
        .toLowerCase();
      const known = {
        'amazon.com': 'Amazon',
        'walmart.com': 'Walmart',
        'target.com': 'Target',
        'instacart.com': 'Instacart',
        'gopuff.com': 'Gopuff',
      };
      for (const domain in known) {
        if (host === domain || host.endsWith('.' + domain)) return known[domain];
      }
      // Fallback: first label of the hostname, capitalized.
      const label = host.split('.')[0];
      return label ? label.charAt(0).toUpperCase() + label.slice(1) : host;
    } catch (e) {
      return null;
    }
  }

  /**
   * Whether analytics tracking is permitted for this visitor, per the Shopify
   * Customer Privacy API. Follows Shopify's documented guard: if the API isn't
   * loaded, tracking may proceed; if it is, gate on the analytics consent flag
   * (falling back to the aggregate userCanBeTracked()). Klaviyo is a
   * third-party platform, so we must not emit when tracking is denied.
   * https://shopify.dev/docs/api/customer-privacy
   */
  isTrackingAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return true; // API not present -> tracking may proceed
    try {
      if (typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
      if (typeof privacy.userCanBeTracked === 'function') {
        return privacy.userCanBeTracked();
      }
    } catch (e) {
      return true;
    }
    return true;
  }

  /**
   * Fires the bev-link click to Klaviyo via onsite tracking (klaviyo.js).
   * Onsite tracking attaches the event to Klaviyo's cookie-based profile
   * (__kla_id), so anonymous clicks merge into the real profile when the
   * visitor later identifies — no manual identifier, no orphan profiles.
   * The queue-safe push form works whether or not klaviyo.js has finished
   * loading. Metric name "Clicked Bev Link" is unchanged for continuity.
   * Gated on the shopper's tracking consent (Shopify Customer Privacy API).
   * @param {string|null} retailerName - The retailer that was clicked.
   * @param {string} ctaType - 'smart_commerce' | 'amazon_direct' | 'custom_button'
   */
  trackBevLinkClick(retailerName, ctaType) {
    // Respect the shopper's tracking consent before emitting to Klaviyo.
    if (!this.isTrackingAllowed()) return;
    const variant = this.currentVariant || {};
    const properties = {
      // Ticket capture list: category, product, variant, retailer.
      bev_category: this.getBevCategory(),
      product_title: (this.productInfo && this.productInfo.title) || null,
      product_handle: (this.productInfo && this.productInfo.handle) || null,
      variant_title: variant.title || null,
      variant_id: variant.id || null,
      retailer_name: retailerName || null,
      // Which CTA fired — supports the "fires on every CTA" verification.
      cta_type: ctaType,
    };
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['track', 'Clicked Bev Link', properties]);
  }

  /**
   * Publishes a one-time "viewed beverage" event to the Shopify customer-events
   * bus on beverage PDP load (fires when this component mounts, i.e. per page
   * view — distinct from the click-driven Klaviyo event above). A custom pixel
   * in Shopify admin (Settings -> Customer events) subscribes to
   * `viewed_beverage` and forwards it to GTM's dataLayer. Kept separate from
   * the GTM app pixel's "viewed product" metric so sessions aren't
   * double-counted under that name. Consent gating happens on the pixel side:
   * Shopify only delivers bus events to pixels the shopper has consented to.
   */
  trackBeverageView() {
    if (this.viewedBeverageTracked) return;
    this.viewedBeverageTracked = true;
    if (!(window.Shopify && window.Shopify.analytics)) return;
    const variant = this.currentVariant || {};
    window.Shopify.analytics.publish('viewed_beverage', {
      viewed_beverage: true,
      bev_category: this.getBevCategory(),
      template_suffix: this.dataset.templateSuffix || null,
      product_title: (this.productInfo && this.productInfo.title) || null,
      product_handle: (this.productInfo && this.productInfo.handle) || null,
      variant_id: variant.id || null,
      variant_title: variant.title || null,
      page_url: window.location.href,
      page_path: window.location.pathname,
    });
  }

  /**
   * Wires bev-link click tracking onto direct buy CTAs that live outside the
   * SmartCommerce widget (the section-level custom buy button). The
   * SmartCommerce dropdown and the per-variant override link are wired
   * elsewhere (renderOptions / getCurrentVariant).
   */
  setupDirectCtaTracking() {
    const customCta = document.querySelector('[data-bev-cta-custom]');
    if (customCta && !customCta.dataset.bevTrackingAttached) {
      customCta.dataset.bevTrackingAttached = 'true';
      customCta.addEventListener('click', () => {
        this.trackBevLinkClick(
          this.getRetailerFromUrl(customCta.getAttribute('href')),
          'custom_button'
        );
      });
    }
  }

  /**
   * Renders the options for the variant button.
   */
  renderOptions() {
    let html = `
            <div class="flex flex-col">`;

    //based on the first selected option, load the variants
    this.selectedVariants = this.variants.filter((variant) => {
      return variant.option1 == this.firstAvailableVariant[0].option1;
    });

    if (this.productInfo.options.length > 1) {
      //render second drop down
      html += `<label for="${this.productInfo.options[1].name}" class="font-bold pb-4 uppercase">Select ${this.productInfo.options[1].name}</label>`;
      html += `<fieldset class="grid grid-cols-4 gap-1.5 pb-6" id="${this.productInfo.options[1].name}">`;
      //loads the option based on the first selected variant

      // Check if there's a pre-selected size from server-side
      const preSelectedSize = this.firstAvailableVariant[0]?.option2;

      if (this.productInfo.options.length == 2) {
        // render only unique variants
        this.returnUniqueVariants(2).forEach((variant) => {
          if (variant.option2 != null) {
            // check if variant already exists in array
            const isChecked = variant.option2 === preSelectedSize;
            html += this.renderLabel(
              variant,
              variant.option2,
              'select-option2',
              isChecked
            );
          }
        });
      } else {
        //if variant has more than two set of options, then don't disable the second level of options
        this.returnUniqueVariants(2).forEach((variant) => {
          if (variant.option2 != null) {
            // check if variant already exists in array
            const isChecked = variant.option2 === preSelectedSize;
            html += this.renderLabel(
              variant,
              variant.option2,
              'select-option2',
              isChecked
            );
          }
        });
      }

      html += `</fieldset>`;
    }

    if (this.productInfo.options.length > 2) {
      //render third drop down

      html += `<label for="${this.productInfo.options[2].name}" class="font-bold py-4 uppercase">Select ${this.productInfo.options[2].name}</label>`;
      html += `<fieldset class="grid grid-cols-4 gap-1.5" id="${this.productInfo.options[2].name}">`;
      //loads the option based on the second selected variant

      this.returnUniqueVariants(3).forEach((variant) => {
        if (variant.option3 != null) {
          html += this.renderLabel(
            variant,
            variant.option3,
            'select-option3',
            false
          );
        }
      });
      html += `</fieldset>`;
    }

    html += `</div>`;

    // Replace the smart cart button block with this updated version and add retailer click tracking
    setTimeout(() => {
      const smartCartButton = document.querySelector('.smart-cart-button');
      if (smartCartButton) {
        const overlay = document.querySelector('.smart-cart-overlay');

        // Only set to "Select Size" if no valid variant is selected
        // If a size is preselected, getCurrentVariant() will be called later to update the button text
        if (overlay) {
          // Check if we have a valid variant based on preselected options
          const hasValidVariant =
            this.firstAvailableVariant &&
            this.firstAvailableVariant[0] &&
            this.firstAvailableVariant[0].option1 &&
            (this.productInfo.options.length === 1 ||
              this.firstAvailableVariant[0].option2);

          // Only set to "Select Size" if we don't have a valid variant
          // If we have a valid variant (including preselected size), getCurrentVariant() will update it
          if (!hasValidVariant) {
            overlay.innerHTML =
              this.getAttribute('default-button-text') || 'Select Size';
          }
          // If hasValidVariant is true, don't set the text here - let getCurrentVariant() handle it
        }

        // Add click tracking to retailer buttons. Fires the "Clicked Bev Link"
        // event directly to Klaviyo via onsite tracking (klaviyo.track) with the
        // enriched properties.
        smartCartButton.addEventListener('click', (e) => {
          const retailerLi = e.target.closest('li');
          if (retailerLi) {
            this.trackBevLinkClick(
              this.getRetailerName(retailerLi),
              'smart_commerce'
            );
          }
        });
      }
    }, 1000); // Add a 1000ms delay to allow the button to load

    this.querySelector('.variant-options-container').innerHTML = html;
  }

  /**
   * Handles the change event for the variant selection.
   * @param {Event} e - The change event object.
   */
  onVariantChange(e) {
    // Store the previous selection before updating this.options
    this.previousOptions = this.options ? [...this.options] : null;

    this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = this.fieldsets.map((fieldset) => {
      const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      );
      return checkedRadio ? checkedRadio.value : null;
    });

    /*--------------------------Event ---------------------------------------*/
    //if the change is on the first set of options
    this.onFirstLevelOfOptionsChange(e);

    /*--------------------------Event ---------------------------------------*/
    //if the change is on the second set of options
    this.onSecondLevelOfOptionsChange(e);

    /*--------------------------Event ---------------------------------------*/
    //get current variant from option changes
    this.getCurrentVariant();
  }

  /**
   * Updates the URL with the size parameter
   * @param {string} sizeValue - The size value to add to URL (null to remove)
   */
  updateSizeUrlParam(sizeValue) {
    const url = new URL(window.location.href);

    if (sizeValue) {
      // Normalize size value: remove spaces, keep hyphens, lowercase
      // "19.2oz - 8 pack" becomes "19.2oz-8pack"
      const normalizedSize = sizeValue.replace(/\s+/g, '').toLowerCase();
      url.searchParams.set('size', normalizedSize);
    } else {
      url.searchParams.delete('size');
    }

    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());

    // Update flavor link hrefs to include the size parameter
    this.updateFlavorLinkHrefs(sizeValue);
  }

  /**
   * Sets up click handlers for flavor links to check for popup before navigation
   */
  setupFlavorLinkPopupHandlers() {
    const flavorLinks = this.querySelectorAll('a[href*="/products/"]');

    flavorLinks.forEach((link) => {
      // Skip if this link already has a popup handler (prevent duplicate listeners)
      if (link.dataset.popupHandlerAttached) return;

      link.addEventListener('click', (e) => {
        // Get the flavor name from the link's 'for' attribute (e.g., "option-Killer Cola")
        const forAttr = link.getAttribute('for');
        if (!forAttr) return;

        // Extract flavor name (remove "option-" prefix)
        const flavorName = forAttr.replace(/^option-/, '');

        // Check if this flavor has popup enabled
        const flavorData = this.flavors[flavorName];

        if (
          flavorData &&
          flavorData.popup_enabled === 'true' &&
          flavorData.klaviyo_form_id
        ) {
          // Prevent navigation
          e.preventDefault();

          // Open Klaviyo form
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(['openForm', flavorData.klaviyo_form_id]);
        }
        // If no popup, allow normal navigation (do nothing, let the link work normally)
      });

      // Mark this link as having a popup handler attached
      link.dataset.popupHandlerAttached = 'true';
    });
  }

  /**
   * Updates all flavor link hrefs to include the current size parameter
   * @param {string} sizeValue - The size value to add to URLs (null to remove)
   */
  updateFlavorLinkHrefs(sizeValue) {
    const flavorLinks = this.querySelectorAll('a[href*="/products/"]');

    flavorLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href && href.includes('/products/')) {
        try {
          const url = new URL(href, window.location.origin);

          if (sizeValue) {
            // Normalize size value: remove spaces, keep hyphens, lowercase
            const normalizedSize = sizeValue.replace(/\s+/g, '').toLowerCase();
            url.searchParams.set('size', normalizedSize);
          } else {
            url.searchParams.delete('size');
          }

          // Update the href (relative path)
          link.setAttribute('href', url.pathname + url.search);
        } catch (e) {
          // If URL parsing fails, skip this link
        }
      }
    });
  }

  /**
   * Handles the change event on the first level of options.
   *
   * @param {Event} e - The event object.
   */
  onFirstLevelOfOptionsChange(e) {
    //if the change is on the first select dropdown
    if (e.target.name == 'select-option1') {
      //only works if variant.option2 is available
      let options = this.fieldsets[1] ? this.fieldsets[1] : null;
      //reset options on update
      this.selectedVariants = this.variants.filter((variant) => {
        return variant.option1 == this.options[0];
      });

      // Update product description based on selected flavor
      const selectedFlavor = this.options[0];
      const flavorData = this.flavors[selectedFlavor];

      if (
        flavorData &&
        flavorData.popup_enabled === 'true' &&
        flavorData.klaviyo_form_id
      ) {
        // Prevent the radio button from being checked
        e.target.checked = false;
        // Restore the previously selected flavor
        if (this.previousOptions && this.previousOptions[0]) {
          const previousRadio = this.querySelector(
            `input[name="select-option1"][value="${this.previousOptions[0]}"]`
          );
          if (previousRadio) {
            previousRadio.checked = true;
          }
        }
        // Open Klaviyo form instead of normal variant handling
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', flavorData.klaviyo_form_id]);
        return;
      }

      // Update product title whenever first option (flavor) changes
      const productTitles = document.querySelectorAll('.product_title');
      productTitles.forEach((title) => {
        // if title contains something that is wrapped with a () split it onto a new line, but keep the brackets
        title.innerHTML = this.options[0].replace(/\((.*?)\)/g, '<br>($1)');
      });

      // Update description based on priority: variant → flavor → default
      this.updateDescription();

      // --- Set default UPC for selected flavor if no size is selected ---

      const shopInStoreButton = document.querySelector('.shop-in-store-button');
      const smartCartButton = document.querySelector('.smart-cart-button');
      const flavorDefaultUpc = this.flavors[selectedFlavor]?.default_upc;
      if (flavorDefaultUpc && (!this.options[1] || this.options[1] === null)) {
        if (shopInStoreButton) {
          let originalHref =
            shopInStoreButton.dataset.originalHref ||
            shopInStoreButton.getAttribute('href');
          if (!shopInStoreButton.dataset.originalHref) {
            shopInStoreButton.dataset.originalHref = originalHref;
          }
          if (originalHref.includes('sc-upc=')) {
            shopInStoreButton.setAttribute(
              'href',
              originalHref.replace(/sc-upc=[^&]+/, 'sc-upc=' + flavorDefaultUpc)
            );
          } else if (originalHref.includes('?')) {
            shopInStoreButton.setAttribute(
              'href',
              originalHref + '&sc-upc=' + flavorDefaultUpc
            );
          } else {
            shopInStoreButton.setAttribute(
              'href',
              originalHref + '?sc-upc=' + flavorDefaultUpc
            );
          }
        }
        if (smartCartButton) {
          smartCartButton.setAttribute('data-sc-upc', flavorDefaultUpc);
        }
      }

      // Get the currently selected size from previous options (before flavor change) or URL parameter
      // Use previousOptions to get the size before the flavor change
      let currentSize =
        this.previousOptions && this.previousOptions[1]
          ? this.previousOptions[1]
          : null;

      // Fallback to URL parameter or currently checked radio if previousOptions doesn't have size
      if (!currentSize) {
        const currentSizeRadio = this.querySelector(
          'input[name="select-option2"]:checked'
        );
        currentSize = currentSizeRadio ? currentSizeRadio.value : null;
      }

      // Final fallback to URL parameter
      if (!currentSize) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlSize = urlParams.get('size');
        if (urlSize) {
          // Try to find matching size option by normalizing
          const matchingSize = this.selectedVariants.find((variant) => {
            if (!variant.option2) return false;
            const normalized = variant.option2
              .replace(/\s+/g, '')
              .toLowerCase();
            return normalized === urlSize;
          });
          if (matchingSize) {
            currentSize = matchingSize.option2;
          }
        }
      }

      // Check if the currently selected size exists for the new flavor
      const sizeExistsForNewFlavor =
        currentSize &&
        this.selectedVariants.some(
          (variant) => variant.option2 === currentSize
        );

      // If size exists for new flavor, set it in options and check the radio button
      if (sizeExistsForNewFlavor && currentSize) {
        this.options[1] = currentSize;
        // Update URL parameter to keep the size
        this.updateSizeUrlParam(currentSize);
      } else {
        // Clear the size from options and URL if it doesn't exist for new flavor
        this.options[1] = null;
        this.updateSizeUrlParam(null);
      }

      if (this.productInfo.options.length > 1) {
        let html = '';
        //assign second set of options

        //if there are three levels of options, do not disable any of the second level options
        if (this.productInfo.options.length > 2) {
          this.returnUniqueVariants(2).forEach((variant) => {
            html += this.renderLabel(
              variant,
              variant.option2,
              'select-option2',
              this.options[1] == variant.option2
            );
          });
        } else {
          //otherwise, disable the second level options if the variant is not available
          this.returnUniqueVariants(2).forEach((variant) => {
            html += this.renderLabel(
              variant,
              variant.option2,
              'select-option2',
              this.options[1] == variant.option2
            );
          });
        }

        options.innerHTML = '';

        options.insertAdjacentHTML('beforeend', html);

        // After rendering, check the size radio button if size exists for new flavor
        if (sizeExistsForNewFlavor && currentSize) {
          const sizeRadio = this.querySelector(
            `input[name="select-option2"][value="${currentSize}"]`
          );
          if (sizeRadio) {
            sizeRadio.checked = true;
            // Update firstAvailableVariant to include the size
            if (this.firstAvailableVariant && this.firstAvailableVariant[0]) {
              this.firstAvailableVariant[0].option2 = currentSize;
            }
            // Trigger change event to update variant and gallery
            sizeRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // If size doesn't exist for new flavor or no variant is found, show the flavor's default image
      if (!sizeExistsForNewFlavor || !this.currentVariant) {
        if (this.flavors[this.options[0]]?.can_image) {
          const changeSlider = new CustomEvent('changeSlider', {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {
              media_id: this.flavors[this.options[0]].can_image,
              transition_speed: 0,
              flavor_size: '12oz',
              flavor_title: this.flavors[this.options[0]].title,
              size_value: '',
            },
          });
          document.dispatchEvent(changeSlider);
        }

        // Reset the smart cart button overlay
        const overlay = document.querySelector('.smart-cart-overlay');
        if (overlay) {
          overlay.innerHTML =
            this.getAttribute('default-button-text') || 'Select Size';
        }
      }

      if (this.productInfo.options.length > 2) {
        //assign third set of options
        let options2 = this.fieldsets[2] ? this.fieldsets[2] : null;

        let html = '';
        this.selectedVariants = this.variants.filter((variant) => {
          return (
            variant.option2 == this.options[1] &&
            variant.option1 == this.options[0]
          );
        });
        this.returnUniqueVariants(3).forEach((variant) => {
          html += this.renderLabel(
            variant,
            variant.option3,
            'select-option3',
            this.options[2] == variant.option3
          );

          options2.innerHTML = '';

          options2.insertAdjacentHTML('beforeend', html);
        });
      }
    }
  }

  /**
   * Handles the change event on the second level of options.
   * @param {Event} e - The event object.
   */
  onSecondLevelOfOptionsChange(e) {
    if (e.target.name == 'select-option2') {
      // Update URL with the selected size
      if (this.options[1]) {
        this.updateSizeUrlParam(this.options[1]);
      } else {
        this.updateSizeUrlParam(null);
      }
      //only works if variant.option2 is available
      let options = this.fieldsets[2] ? this.fieldsets[2] : null;
      //reset options on update
      this.selectedVariants = this.variants.filter((variant) => {
        return (
          variant.option2 == this.options[1] &&
          variant.option1 == this.options[0]
        );
      });

      //assign third set of options
      if (this.productInfo.options.length > 2) {
        let html = '';

        this.returnUniqueVariants(3).forEach((variant) => {
          if (variant.option3 != null) {
            html += this.renderLabel(
              variant,
              variant.option3,
              'select-option3',
              this.options[2] == variant.option3
            );
          }
          options.innerHTML = '';

          options.insertAdjacentHTML('beforeend', html);
        });
      }
    }
  }

  /**
   * Updates the product description based on priority:
   * 1. Variant-level description (if variant is selected and has description)
   * 2. Flavor-level description (if variant description doesn't exist but flavor has description)
   * 3. Default product description (if neither variant nor flavor descriptions exist)
   */
  updateDescription() {
    const descriptionElement = document.querySelector('.description');
    if (!descriptionElement) return;

    // Priority 1: Check if current variant has a description
    if (
      this.currentVariant &&
      this.currentVariant.description &&
      this.currentVariant.description.trim() !== ''
    ) {
      descriptionElement.innerHTML = this.currentVariant.description;
      this.decorateKosherBullet(descriptionElement);
      return;
    }

    // Priority 2: Check if selected flavor has a description
    // Get selected flavor from options if available, otherwise from checked radio or firstAvailableVariant
    let selectedFlavor = null;
    if (this.options && this.options[0]) {
      selectedFlavor = this.options[0];
    } else {
      const checkedFlavorRadio = this.querySelector(
        'input[name="select-option1"]:checked'
      );
      if (checkedFlavorRadio) {
        selectedFlavor = checkedFlavorRadio.value;
      } else if (
        this.firstAvailableVariant &&
        this.firstAvailableVariant[0] &&
        this.firstAvailableVariant[0].option1
      ) {
        selectedFlavor = this.firstAvailableVariant[0].option1;
      }
    }

    if (selectedFlavor) {
      const flavorData = this.flavors[selectedFlavor];
      if (
        flavorData &&
        flavorData.description &&
        flavorData.description.trim() !== ''
      ) {
        descriptionElement.innerHTML = flavorData.description;
        this.decorateKosherBullet(descriptionElement);
        return;
      }
    }

    // Priority 3: Use default product description
    // The attribute carries the same /quote/ and /apos/ placeholders as the
    // JSON attributes, but is consumed raw — decode to literal characters.
    const defaultDescription = this.getAttribute('js-default-description');
    if (defaultDescription) {
      descriptionElement.innerHTML = defaultDescription
        .replace(/\/quote\//g, '"')
        .replace(/\/apos\//g, "'");
    }
    this.decorateKosherBullet(descriptionElement);
  }

  /**
   * If product is kosher certified, add a "Certified Kosher" bullet with
   * the OU badge image to the last <ul> in the description. The
   * "Packaging & Formula May Vary" bullet must stay the very last bullet,
   * so kosher inserts before it when present, otherwise appends at the
   * end. Does NOT modify any existing description content. Re-runs on
   * every description swap.
   */
  decorateKosherBullet(descriptionElement) {
    if (descriptionElement.dataset.kosherCertified !== 'true') return;

    const lastUl = descriptionElement.querySelector('ul:last-of-type');
    if (!lastUl) return;
    if (lastUl.querySelector('li[data-kosher-appended="true"]')) return;

    const badgeSrc = descriptionElement.dataset.kosherBadge;
    const badgeHtml = badgeSrc
      ? `<img src="${badgeSrc}" alt="Certified Kosher OU" width="20" height="20" loading="lazy" class="inline-block w-5 h-5 object-contain align-middle ml-1">`
      : '';

    const li = document.createElement('li');
    li.dataset.kosherAppended = 'true';
    li.innerHTML = `<span class="align-middle">Certified Kosher</span>${badgeHtml}`;

    const packagingLi = Array.from(lastUl.querySelectorAll('li')).find((el) =>
      /packaging\s*(?:&|and)\s*formula\s*may\s*vary/i.test(el.textContent)
    );
    if (packagingLi) {
      lastUl.insertBefore(li, packagingLi);
    } else {
      lastUl.appendChild(li);
    }
  }

  /**
   * Retrieves the current variant based on the selected options.
   * If the current variant doesn't exist, it updates the product cta and disables it.
   * If the current variant is available, it updates the product cta and enables it.
   * It also updates the price, gallery image, and sets the data-variant attribute.
   */
  getCurrentVariant() {
    //get current variant from selected options
    this.currentVariant = this.variants.find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });

    // Get smartCartButton reference once at the start
    const smartCartButton = document.querySelector('.smart-cart-button');
    const overlay = document.querySelector('.smart-cart-overlay');

    // --- Disable button if no valid variant is selected ---
    if (!this.currentVariant) {
      if (smartCartButton && overlay) {
        smartCartButton.classList.add('pointer-events-none');
        overlay.classList.add('pointer-events-none');
        overlay.classList.remove('cursor-pointer');
        overlay.innerHTML =
          this.getAttribute('default-button-text') || 'Select Size';
      }
      return;
    }

    if (this.currentVariant) {
      // Update description based on priority: variant → flavor → default
      this.updateDescription();

      const tag = document.querySelector('.variant-tag');

      if (this.currentVariant.tag) {
        tag.innerHTML = `<span class="block relative -mb-[2px]">${this.currentVariant.tag}</span>`;
        tag.classList.remove('hidden');
      } else {
        tag.classList.add('hidden');
      }

      const buttonUrl = this.currentVariant.button_url_override;
      const buttonText = this.currentVariant.button_text_override;

      // Check if variant has Klaviyo popup enabled (can size level)
      if (
        this.currentVariant.popup === 'true' &&
        this.currentVariant.klaviyo_form_id
      ) {
        smartCartButton.classList.add('pointer-events-none');
        overlay.classList.remove('pointer-events-none');
        overlay.classList.add('cursor-pointer');
        if (overlay) {
          overlay.innerHTML = buttonText || 'NOTIFY ME';
          overlay.onclick = (e) => {
            e.preventDefault();
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push([
              'openForm',
              this.currentVariant.klaviyo_form_id,
            ]);
          };
        }
        return;
      }

      if (buttonText && !buttonUrl) {
        smartCartButton.classList.add('pointer-events-none');
        overlay.classList.add('pointer-events-none');
        overlay.classList.remove('cursor-pointer');
        if (overlay) {
          overlay.innerHTML = buttonText;
        }
      } else if (buttonText && buttonUrl) {
        smartCartButton.classList.add('pointer-events-none');
        overlay.classList.remove('pointer-events-none');
        overlay.classList.add('cursor-pointer');
        if (overlay) {
          overlay.innerHTML = buttonText;
          overlay.onclick = (e) => {
            e.preventDefault();
            // Direct-to-retailer link (e.g. Amazon) — bypasses SmartCommerce,
            // so fire the bev-link click here too.
            this.trackBevLinkClick(
              this.getRetailerFromUrl(buttonUrl),
              'amazon_direct'
            );
            window.open(buttonUrl, '_blank', 'noopener,noreferrer');
          };
        }
      } else if (this.currentVariant.barcode) {
        // If there is no text override or button link, but there is a barcode
        smartCartButton.setAttribute(
          'data-sc-upc',
          this.currentVariant.barcode
        );
        if (typeof SmartCart !== 'undefined' && SmartCart.updateUsaWidget) {
          SmartCart.updateUsaWidget();
        }
        smartCartButton.classList.remove('pointer-events-none');
        overlay.classList.add('pointer-events-none');
        if (overlay) {
          overlay.innerHTML = 'BUY NOW';
        }
      }

      // Update the shop-in-store-button URL with barcode if it exists.
      const shopInStoreButton = document.querySelector('.shop-in-store-button');
      if (shopInStoreButton) {
        // Store the original href if not already stored.
        if (!shopInStoreButton.dataset.originalHref) {
          shopInStoreButton.dataset.originalHref =
            shopInStoreButton.getAttribute('href');
        }
        if (this.currentVariant.barcode) {
          const originalHref = shopInStoreButton.dataset.originalHref;
          if (originalHref.includes('sc-upc=')) {
            // Replace existing sc-upc parameter
            shopInStoreButton.setAttribute(
              'href',
              originalHref.replace(
                /sc-upc=[^&]+/,
                'sc-upc=' + this.currentVariant.barcode
              )
            );
          } else if (originalHref.includes('?')) {
            // Add sc-upc as additional parameter
            shopInStoreButton.setAttribute(
              'href',
              originalHref + '&sc-upc=' + this.currentVariant.barcode
            );
          } else {
            // Add sc-upc as first parameter
            shopInStoreButton.setAttribute(
              'href',
              originalHref + '?sc-upc=' + this.currentVariant.barcode
            );
          }
        } else {
          // If barcode is not available, revert to the original href.
          shopInStoreButton.setAttribute(
            'href',
            shopInStoreButton.dataset.originalHref
          );
        }
      }

      // Update gallery image on variant change
      if (this.currentVariant.featured_image != null) {
        // if this.currentVariant.title contains 12oz, then set the flavor size to 12oz or if it contains 19.2oz, set it to 19.2oz

        let flavorSize = '12oz';
        if (this.currentVariant.title.includes('19.2oz')) {
          flavorSize = '19.2oz';
        }

        // Use option1 (flavor name) instead of full variant title for accurate matching
        // Normalize to handle HTML entities and special characters like "ñ"
        let flavorName =
          this.currentVariant.option1 ||
          this.currentVariant.title.split(' / ')[0];

        // Decode HTML entities if present (e.g., &ntilde; -> ñ)
        if (flavorName) {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = flavorName;
          flavorName = textarea.value.normalize('NFC').trim();
        }

        const changeSlider = new CustomEvent('changeSlider', {
          bubbles: true,
          cancelable: true,
          composed: true,
          detail: {
            media_id: this.currentVariant.featured_image,
            transition_speed: 0,
            flavor_size: flavorSize,
            flavor_title: flavorName,
            // Raw size option value — the gallery skips front-of-can slides
            // for "* - Single" variants (their image already IS the can front).
            size_value: this.currentVariant.option2 || '',
          },
        });

        document.dispatchEvent(changeSlider);
      }
    }
  }

  /**
   * Returns an array of unique variants based on the specified option index.
   * @param {number} optionIndex - The index of the option to filter variants by.
   * @returns {Array} - An array of unique variants.
   */
  //This function is to return unique variant options, so that the options are not duplicated
  returnUniqueVariants(optionIndex) {
    const uniqueVariants = [];
    this.selectedVariants.forEach((variant) => {
      if (variant[`option${optionIndex}`] != null) {
        const exists = uniqueVariants.some(
          (v) => v[`option${optionIndex}`] === variant[`option${optionIndex}`]
        );
        if (!exists) {
          uniqueVariants.push(variant);
        }
      }
    });
    return uniqueVariants;
  }

  /**
   * Renders the label for a variant button.
   *
   * @param {string} variantOption - The variant option.
   * @param {string} selectOption - The select option.
   * @param {boolean} checked - Indicates if the variant is checked.
   * @returns {string} The rendered label HTML.
   */
  //This function renders the button input and label
  renderLabel(variant, variantOption, selectOption, checked) {
    // Default rendering for all other options
    let swatchStyle = `button-swatch text-center flex flex-col items-center justify-center gap-3 border border-lightGray rounded-lg`;
    let labelContent = variantOption;

    if (variantOption.includes('-')) {
      const [firstPart, secondPart] = variantOption
        .split('-')
        .map((part) => part.trim());

      labelContent = `<span class="text-mdSmall font-clarendon">${firstPart}</span><span class="text-base font-medium">${secondPart}</span>`;
    }

    // Check if variant should be disabled based on disable_size metafield
    const shouldDisable = variant.disable_size === 'true';

    const opacityClass = shouldDisable ? 'opacity-50' : '';
    const disabledClass = shouldDisable
      ? 'pointer-events-none cursor-not-allowed'
      : '';
    const disabledAttr = shouldDisable ? 'disabled' : '';

    return `
        <input ${checked ? 'checked' : ''} 
          class="product__variant--button hidden" 
          name="${selectOption}" 
          id="option-${variantOption}" 
          type="radio" 
          value="${variantOption}"
          ${disabledAttr}
        >
        <label 
          class="p-3 ${swatchStyle} ${opacityClass} ${disabledClass}" 
          for="option-${variantOption}"
          tabindex="${shouldDisable ? '-1' : '0'}"
          role="radio"
          aria-checked="${checked ? 'true' : 'false'}"
          aria-label="${variantOption}"
          aria-disabled="${shouldDisable ? 'true' : 'false'}"
        >
          ${labelContent}
        </label>
      `;
  }
}
customElements.define('variant-button', VariantButton);
